import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    DatePicker,
    Descriptions,
    message,
    Modal,
    Space,
    Spin,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid, { type WebGridColumn } from '../../../webgrid/src';
import {
    buildSessionVerificationDetailDownload,
    buildSessionVerificationParams,
    buildSessionVerificationsDownload,
    type DateRange,
    formatLeiWhole,
    formatVerificationProgress,
    getDefaultHandoverRange,
    isSignificantVerificationDifference,
    type ReportDownload,
    type ReportFormat,
    type SessionVerificationContract,
    type SessionVerificationDetail,
    type SessionVerificationSummary,
} from '../../domain/controlling';
import { withCurrency } from '../../domain/controlRoom';
import useFileDownload from '../../hooks/useFileDownload';
import useRequest from '../../hooks/useRequest';

const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';
const SIGNIFICANT_ROW_STYLE = { backgroundColor: '#fff1f0' };
const SESSION_SUMMARIES = {
    differenceContractCount: 'sum',
    differenceRon: 'sum',
    differenceEur: 'sum',
} as const;

interface SessionVerificationsResponse {
    success: boolean;
    data: { sessions: SessionVerificationSummary[] };
}

interface SessionVerificationDetailResponse {
    success: boolean;
    data: SessionVerificationDetail;
}

const renderWhole = (value: unknown) => formatLeiWhole(value as number);
const renderDash = (value: unknown) => (value as string | null) ?? '-';
const renderVerifiedFlag = (value: unknown) => <Checkbox checked={Boolean(value)} disabled />;

const getDifferenceRowStyle = (row: { differenceRon: number }) =>
    isSignificantVerificationDifference(row.differenceRon) ? SIGNIFICANT_ROW_STYLE : undefined;

interface ExportButtonsProps {
    busyFormat: ReportFormat | null;
    disabled: boolean;
    testIdPrefix: string;
    onExport: (format: ReportFormat) => void;
}

const ExportButtons = ({ busyFormat, disabled, testIdPrefix, onExport }: ExportButtonsProps) => {
    const { t } = useTranslation();
    return (
        <>
            <Button
                icon={<FileExcelOutlined />}
                loading={busyFormat === 'xlsx'}
                disabled={disabled}
                onClick={() => onExport('xlsx')}
                data-testid={`${testIdPrefix}-export-xlsx`}
            >
                {t('controlRoom.controlling.exportExcel')}
            </Button>
            <Button
                icon={<FilePdfOutlined />}
                loading={busyFormat === 'pdf'}
                disabled={disabled}
                onClick={() => onExport('pdf')}
                data-testid={`${testIdPrefix}-export-pdf`}
            >
                {t('controlRoom.controlling.exportPdf')}
            </Button>
        </>
    );
};

/**
 * Report of the previous-session verifications: one row per verified cash session
 * with its differences, operators and verifiers; clicking a session opens the
 * contracts re-assessed for it. Both views download as Excel or PDF.
 */
const SessionVerificationsPanel = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const downloadFile = useFileDownload();
    const [range, setRange] = useState<DateRange>(() => getDefaultHandoverRange(new Date()));
    const [onlyDifferences, setOnlyDifferences] = useState(false);
    const [sessions, setSessions] = useState<SessionVerificationSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<ReportFormat | null>(null);
    const [selectedSession, setSelectedSession] = useState<SessionVerificationSummary | null>(
        null,
    );
    const [detail, setDetail] = useState<SessionVerificationDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailExportingFormat, setDetailExportingFormat] = useState<ReportFormat | null>(null);
    // Session whose contracts were asked for last, so a slower earlier answer is dropped.
    const requestedSessionNumber = useRef<number | null>(null);

    const loadSessions = useCallback(
        async (period: DateRange, differencesOnly: boolean) => {
            setLoading(true);
            try {
                const result = await request<SessionVerificationsResponse>({
                    path: '/controlling/session-verifications',
                    params: buildSessionVerificationParams(period, differencesOnly),
                });
                setSessions(result.data.sessions);
            } catch (error) {
                message.error(t('controlRoom.controlling.sessionVerificationsLoadError'));
                console.error('Failed to load session verifications', error);
            } finally {
                setLoading(false);
            }
        },
        [request, t],
    );

    useEffect(() => {
        void loadSessions(range, onlyDifferences);
    }, [loadSessions, range, onlyDifferences]);

    const openSession = async (session: SessionVerificationSummary) => {
        requestedSessionNumber.current = session.sessionNumber;
        setSelectedSession(session);
        setDetail(null);
        setDetailLoading(true);
        try {
            const result = await request<SessionVerificationDetailResponse>({
                path: `/controlling/session-verifications/${session.sessionNumber}`,
            });
            if (requestedSessionNumber.current === session.sessionNumber) {
                setDetail(result.data);
            }
        } catch (error) {
            message.error(t('controlRoom.controlling.sessionVerificationDetailLoadError'));
            console.error('Failed to load session verification', error);
        } finally {
            if (requestedSessionNumber.current === session.sessionNumber) {
                setDetailLoading(false);
            }
        }
    };

    const runDownload = async (
        download: ReportDownload,
        format: ReportFormat,
        setBusy: (format: ReportFormat | null) => void,
    ) => {
        setBusy(format);
        try {
            await downloadFile(download.path, download.filename, download.mimeType);
            message.success(t('common.exportSuccess'));
        } catch (error) {
            message.error(t('controlRoom.controlling.sessionVerificationsExportError'));
            console.error('Failed to export session verifications', error);
        } finally {
            setBusy(null);
        }
    };

    const exportSessions = (format: ReportFormat) => {
        void runDownload(
            buildSessionVerificationsDownload(range, onlyDifferences, format),
            format,
            setExportingFormat,
        );
    };

    const exportDetail = (format: ReportFormat) => {
        if (selectedSession) {
            void runDownload(
                buildSessionVerificationDetailDownload(selectedSession.sessionNumber, format),
                format,
                setDetailExportingFormat,
            );
        }
    };

    const sessionColumns = useMemo<WebGridColumn<SessionVerificationSummary>[]>(
        () => [
            {
                key: 'sessionNumber',
                title: t('controlRoom.controlling.sessionNumber'),
                dataIndex: 'sessionNumber',
                sortable: true,
                filterable: true,
            },
            {
                key: 'cashPointName',
                title: t('controlRoom.controlling.cashPoint'),
                dataIndex: 'cashPointName',
                filterable: true,
                render: renderDash,
            },
            {
                key: 'sessionStart',
                title: t('controlRoom.controlling.sessionStart'),
                dataIndex: 'sessionStart',
                sortable: true,
                render: renderDash,
            },
            {
                key: 'operators',
                title: t('controlRoom.controlling.operators'),
                dataIndex: 'operators',
                sortable: true,
                filterable: true,
                render: renderDash,
            },
            {
                key: 'verifiers',
                title: t('controlRoom.controlling.verifiers'),
                dataIndex: 'verifiers',
                sortable: true,
                filterable: true,
                render: renderDash,
            },
            {
                key: 'verifiedCount',
                title: t('controlRoom.controlling.verifiedContracts'),
                dataIndex: 'verifiedCount',
                align: 'right',
                render: (_value: unknown, session: SessionVerificationSummary) =>
                    formatVerificationProgress(session),
            },
            {
                key: 'differenceContractCount',
                title: t('controlRoom.controlling.differenceContracts'),
                dataIndex: 'differenceContractCount',
                align: 'right',
                sortable: true,
            },
            {
                key: 'differenceRon',
                title: withCurrency(t('controlRoom.controlling.difference'), 'LEI'),
                dataIndex: 'differenceRon',
                align: 'right',
                sortable: true,
                render: renderWhole,
            },
            {
                key: 'differenceEur',
                title: withCurrency(t('controlRoom.controlling.difference'), 'EUR'),
                dataIndex: 'differenceEur',
                align: 'right',
                sortable: true,
                render: renderWhole,
            },
            {
                key: 'lastVerifiedAt',
                title: t('controlRoom.controlling.lastVerifiedAt'),
                dataIndex: 'lastVerifiedAt',
                sortable: true,
                render: renderDash,
            },
        ],
        [t],
    );

    const contractColumns = useMemo<WebGridColumn<SessionVerificationContract>[]>(
        () => [
            {
                key: 'contractNumber',
                title: t('controlRoom.controlling.contractNumber'),
                dataIndex: 'contractNumber',
                sortable: true,
                filterable: true,
                render: renderDash,
            },
            {
                key: 'objectDescription',
                title: t('controlRoom.controlling.object'),
                dataIndex: 'objectDescription',
                filterable: true,
                render: renderDash,
            },
            {
                key: 'loanAmount',
                title: withCurrency(t('controlRoom.controlling.loanAmount'), 'LEI'),
                dataIndex: 'loanAmount',
                align: 'right',
                render: renderWhole,
            },
            {
                key: 'differenceRon',
                title: withCurrency(t('controlRoom.controlling.difference'), 'LEI'),
                dataIndex: 'differenceRon',
                align: 'right',
                sortable: true,
                render: renderWhole,
            },
            {
                key: 'differenceEur',
                title: withCurrency(t('controlRoom.controlling.difference'), 'EUR'),
                dataIndex: 'differenceEur',
                align: 'right',
                sortable: true,
                render: renderWhole,
            },
            {
                key: 'verified',
                title: t('controlRoom.controlling.verified'),
                dataIndex: 'verified',
                align: 'center',
                render: renderVerifiedFlag,
            },
            {
                key: 'verifiedBy',
                title: t('controlRoom.controlling.verifiers'),
                dataIndex: 'verifiedBy',
                filterable: true,
                render: renderDash,
            },
            {
                key: 'verifiedAt',
                title: t('controlRoom.controlling.verifiedAt'),
                dataIndex: 'verifiedAt',
                sortable: true,
                render: renderDash,
            },
            {
                key: 'notes',
                title: t('controlRoom.controlling.notes'),
                dataIndex: 'notes',
                filterable: true,
                render: renderDash,
            },
        ],
        [t],
    );

    const shownSession = detail?.session ?? selectedSession;

    return (
        <div data-testid="controlling-session-verifications">
            <Space style={{ marginBottom: 12 }} wrap>
                <Text>{t('controlRoom.controlling.from')}</Text>
                <DatePicker
                    aria-label={t('controlRoom.controlling.from')}
                    value={dayjs(range.startDate)}
                    format={DATE_FORMAT}
                    allowClear={false}
                    onChange={value => {
                        if (value) {
                            setRange({ ...range, startDate: value.format(DATE_FORMAT) });
                        }
                    }}
                />
                <Text>{t('controlRoom.controlling.to')}</Text>
                <DatePicker
                    aria-label={t('controlRoom.controlling.to')}
                    value={dayjs(range.endDate)}
                    format={DATE_FORMAT}
                    allowClear={false}
                    onChange={value => {
                        if (value) {
                            setRange({ ...range, endDate: value.format(DATE_FORMAT) });
                        }
                    }}
                />
                <Checkbox
                    checked={onlyDifferences}
                    onChange={event => setOnlyDifferences(event.target.checked)}
                >
                    {t('controlRoom.controlling.onlyDifferences')}
                </Checkbox>
                <Button type="primary" onClick={() => loadSessions(range, onlyDifferences)}>
                    {t('controlRoom.controlling.show')}
                </Button>
                <ExportButtons
                    busyFormat={exportingFormat}
                    disabled={sessions.length === 0}
                    testIdPrefix="controlling-session-verifications"
                    onExport={exportSessions}
                />
                <Text type="secondary" data-testid="controlling-session-verifications-count">
                    {t('controlRoom.controlling.sessionCount', { count: sessions.length })}
                </Text>
            </Space>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {t('controlRoom.controlling.sessionVerificationsHint')}
            </Text>
            <WebGrid<SessionVerificationSummary>
                data={sessions}
                columns={sessionColumns}
                loading={loading}
                filterBar
                pagination
                pageSize={50}
                footer
                columnSummaries={SESSION_SUMMARIES}
                onRowClick={openSession}
                getRowStyle={getDifferenceRowStyle}
                rowIdAccessor={session => session.sessionNumber}
                height="calc(100vh - 360px)"
                emptyMessage={t('controlRoom.controlling.noSessionVerifications')}
            />

            <Modal
                open={selectedSession !== null}
                title={t('controlRoom.controlling.sessionVerificationTitle', {
                    session: selectedSession?.sessionNumber ?? '',
                })}
                width={1200}
                onCancel={() => setSelectedSession(null)}
                destroyOnHidden
                footer={[
                    <ExportButtons
                        key="export"
                        busyFormat={detailExportingFormat}
                        disabled={detail === null}
                        testIdPrefix="controlling-session-verification-detail"
                        onExport={exportDetail}
                    />,
                    <Button key="close" onClick={() => setSelectedSession(null)}>
                        {t('common.close')}
                    </Button>,
                ]}
            >
                <div data-testid="controlling-session-verification-detail">
                    {shownSession && (
                        <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered>
                            <Descriptions.Item label={t('controlRoom.controlling.cashPoint')}>
                                {shownSession.cashPointName ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('controlRoom.controlling.sessionStart')}>
                                {shownSession.sessionStart ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('controlRoom.controlling.sessionEnd')}>
                                {shownSession.sessionEnd ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('controlRoom.controlling.operators')}>
                                {shownSession.operators ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('controlRoom.controlling.verifiers')}>
                                {shownSession.verifiers ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('controlRoom.controlling.verifiedContracts')}
                            >
                                {formatVerificationProgress(shownSession)}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('controlRoom.controlling.differenceContracts')}
                            >
                                {shownSession.differenceContractCount}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={withCurrency(t('controlRoom.controlling.difference'), 'LEI')}
                            >
                                <span data-testid="controlling-session-verification-difference-ron">
                                    {formatLeiWhole(shownSession.differenceRon)}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={withCurrency(t('controlRoom.controlling.difference'), 'EUR')}
                            >
                                {formatLeiWhole(shownSession.differenceEur)}
                            </Descriptions.Item>
                        </Descriptions>
                    )}
                    <Spin spinning={detailLoading}>
                        <div style={{ marginTop: 12 }}>
                            <WebGrid<SessionVerificationContract>
                                data={detail?.contracts ?? []}
                                columns={contractColumns}
                                filterBar
                                getRowStyle={getDifferenceRowStyle}
                                rowIdAccessor={contract => contract.id}
                                height="50vh"
                                emptyMessage={t('controlRoom.controlling.noSessionContracts')}
                            />
                        </div>
                    </Spin>
                </div>
            </Modal>
        </div>
    );
};

export default SessionVerificationsPanel;
